/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { MdOutlineWifi, MdOutlineWifiOff } from 'react-icons/md'

import type { InStoreGateway, Position } from '../types'

export interface GatewayMarkerProps {
  gateway: InStoreGateway
  locationName: string
  position: Position
  /** Side in px, in step with the sensor markers. */
  size: number
  editMode?: boolean
  handlers?: React.DOMAttributes<HTMLDivElement>
}

/** The location's gateway on the floor plan: a wifi icon, green online and gray offline. */
export const GatewayMarker = ({ gateway, locationName, position, size, editMode, handlers }: GatewayMarkerProps) => (
  <div
    data-testid={`marker-gateway-${gateway.umdId}`}
    data-online={gateway.online}
    role="img"
    aria-label={`${locationName} gateway ${gateway.online ? 'online' : 'offline'}`}
    {...handlers}
    style={{
      left: `${position.x * 100}%`,
      top: `${position.y * 100}%`,
      width: size,
      height: size,
      touchAction: 'none',
    }}
    className={`group absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center rounded-lg bg-base-100 shadow ring-2 select-none z-10 hover:z-30 ${
      gateway.online ? 'ring-green-500 text-green-500' : 'ring-gray-400 text-gray-500'
    } ${editMode ? 'cursor-move' : ''}`}
  >
    {gateway.online ? (
      <MdOutlineWifi style={{ width: size * 0.55, height: size * 0.55 }} />
    ) : (
      <MdOutlineWifiOff style={{ width: size * 0.55, height: size * 0.55 }} />
    )}
    <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 whitespace-nowrap rounded bg-base-100 px-2 py-0.5 text-xs text-base-content shadow opacity-0 transition-opacity group-hover:opacity-100">
      {gateway.deviceName}
      <span className="text-base-content/60"> · {locationName}</span>
    </span>
  </div>
)
